import React, { useCallback, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography, styled } from '@mui/material';
import { Chart, Plan, Legend as LegendModel } from '../../models';
import { ChartService } from '../../services';
import Legend from '../shared/Legend';

const ChartContainer = styled('div')`
  & > div:not(:last-child) {
    margin-bottom: 25px;
  }
`;

const ChartHolder = styled('div')`
  width: 100%;
  height: 150px;

  // force override react-chartjs-2 auto width values
  canvas {
    width: 100% !important;
  }
`;

const ConfirmationLegend = styled(Legend)`
  margin-top: 8px;

  ul {
    justify-content: space-evenly;
  }

  span {
    height: 20px;
    width: 40px;
    margin-right : 5px;
  }

  div {
    display: inline;
  }
`;

type Props = {
  chartData: Plan;
  selectedOption: string;
}

function CreateChart({ chartData, selectedOption }: Props) {
  const intl = useIntl();

  let selectedOptionChartData = useMemo(() => (
    chartData?.options.filter((opt) => opt.optionLabel === selectedOption)[0]
  ), [chartData?.options, selectedOption]);

  const mappedTicks = useMemo((): string[] => {
    // eslint-disable-next-line max-len
    const referenceArray = ['11PM', '12AM', '1AM', '2AM', '3AM', '4AM', '5AM', '6AM', '7AM', '8AM', '9AM', '10AM', '11AM', '12PM', '1PM', '2PM', '3PM', '4PM', '5PM', '6PM', '7PM', '8PM', '9PM', '10PM', '11PM'];
    const tickArray = ['11PM', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '11PM'];
    let indexCounter = 0;
    selectedOptionChartData.charts[0].datasets.forEach((dataset) => {
      indexCounter += dataset.data[0];
      const referenceVal = referenceArray[indexCounter];
      tickArray[indexCounter] = referenceVal;
    });
    return tickArray;
  }, [selectedOptionChartData.charts]);

  const chartType = useMemo(() => selectedOptionChartData.charts.map((chart) => {
    if (chart.labels[0] === '') {
      return 'stacked';
    }
    return 'bar';
  }), [selectedOptionChartData.charts]);

  const optionType = useMemo(() => {
    const hasOnlyEmptyArrays = chartType.every((type) => type === 'stacked');
    const hasMixedArrays = chartType.some((type) => type === 'stacked');
    if (hasOnlyEmptyArrays) return 'stacked';
    if (hasMixedArrays) return 'mixed';
    return 'bars';
  }, [chartType]);

  const barChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          crossAlign: 'far',
        },
      },
      y: {
        min: 0,
        max: 0.20,
        ticks: {
          stepSize: 0.04,
          callback(value) {
            return `$${value}/kWh`;
          },
        },
      },
    },
    plugins: {
      datalabels: {
        anchor: 'end',
        align: 'end',
        formatter(value) {
          return `$${value.toFixed(2)}/kWh`;
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  }), []);

  const stackedChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    animation: false,
    // barThickness: 25,
    scales: {
      x: {
        type: 'linear',
        stacked: true,
        min: 0,
        max: 24,
        ticks: {
          labelOffset: -6,
          count: 25,
          callback(value, index) {
            return mappedTicks[index];
          },
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90,
        },
        grid: {
          drawOnChartArea: false,
          drawBorder: true,
          drawTicks: true,
          tickWidth: 5,
          tickLength: 8,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  }), [mappedTicks]);

  selectedOptionChartData = useMemo(() => ChartService.mapLabels(selectedOptionChartData, intl), [intl, selectedOptionChartData]);
  const selectedOptionLegendData = useMemo(() => ChartService.mapLegends(selectedOptionChartData, intl), [intl, selectedOptionChartData]);

  const renderChart = useCallback((key: number, data: Chart, chartOptions: ChartOptions<'bar'>, legendData: LegendModel[] | null) => (
    <div key={key}>
      <ChartHolder>
        <Bar
          data={data}
          options={chartOptions}
          plugins={chartOptions === stackedChartOptions ? [] : [ChartDataLabels]}
          aria-label="usageChart"
        />
      </ChartHolder>
      {legendData && <ConfirmationLegend legendItems={legendData} />}
    </div>
  ), [stackedChartOptions]);

  return (
    <>
      <Typography variant="h5" gutterBottom id="ChartHeader">
        <FormattedMessage id={selectedOptionChartData.optionTitle} />
      </Typography>
      <ChartContainer>
        {selectedOptionChartData && selectedOptionChartData.charts.map((chart: Chart, i: number) => {
          if (optionType === 'stacked') {
            // case when all the charts under the current option are stacked
            return renderChart(i, chart, stackedChartOptions, selectedOptionLegendData[i]);
          }
          if (optionType === 'mixed') {
            // case when there is a mix of chart types under the current option
            if (chartType[i] === 'stacked') {
              // case when the currently iterated chart is a stacked chart
              return renderChart(i, chart, stackedChartOptions, null);
            }
            if (chartType[i] === 'bar') {
              // case when the currently iterated chart is a bar chart
              return renderChart(i, chart, barChartOptions, selectedOptionLegendData[i]);
            }
          }
          return null;
        })}

      </ChartContainer>
    </>
  );
}

export default CreateChart;
